import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getAfpRouting, sendAfpRouting } from '../api';
import { formatMoney } from '../lib/format';

/* ──────────────────────────────────────────────────────────────────
 * AFP Signature Routing — "who is signing this one, and who is
 * notarising it?"
 *
 * WHY A PAGE AND NOT A SETTING. The signer and the notary were two
 * fixed values in the CE Agent dashboard, so every pay application
 * went to the same pair whether or not either of them was in the
 * building. They have to be physically together to notarise. So this
 * is a decision somebody makes per application, on the day.
 *
 * WHO SEES THIS. Whoever is on the accounting list gets emailed a
 * link when a pay application is generated, and again every 8 hours
 * until they answer. They are always inside E3, so they are already
 * logged into Summit; the link carries the token that says WHICH
 * application, and login says who they are.
 *
 * BOTH FIELDS ARE FREE TEXT, with the configured people offered as a
 * datalist. That is deliberate and it is the requirement: if none of
 * the usual people are around, somebody else has to be able to do
 * it. The list is a convenience, never a whitelist.
 *
 * THE ONE-WAY DOOR. Submitting sends a real Dropbox Sign envelope to
 * two real people. So the button says what it does, the summary of
 * who gets what is visible above it, and a request that has already
 * been sent renders as a receipt rather than as a form -- there is no
 * second envelope to be had from this screen.
 * ────────────────────────────────────────────────────────────────── */

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());

const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/**
 * The one rule this screen enforces, as a pure function so it can be asserted
 * without a browser: two valid addresses, belonging to two different people.
 * Returns the sentence to show, or null when the pair is sendable.
 *
 * The server checks this too. That is not redundancy for its own sake -- this
 * is the copy a person actually reads, and the server's job is to be
 * unbypassable rather than to be well-written.
 */
export function routingProblem(signer, notary) {
  if (!isEmail(signer)) return 'Enter a valid email address for the person signing.';
  if (!isEmail(notary)) return 'Enter a valid email address for the notary.';
  if (same(signer, notary)) {
    return 'The signer and the notary have to be two different people \u2014 a '
      + 'notary cannot notarise their own signature.';
  }
  return null;
}

/**
 * What the two fields start as. Pure, because getting this wrong is silent:
 * seeding both slots with the same person hands the user a form that cannot be
 * submitted and does not say why until they touch it.
 *
 * An already-chosen pair wins (a request that came back from a failed send
 * keeps what was picked). Otherwise: position A's first configured person
 * signs, and the first notary who is NOT that person notarises.
 */
export function seedPair(request, viewerEmail) {
  const req = request || {};
  const a = (req.position_a_options || [])[0] || req.position_a_email || '';
  const signer = req.signer_email || a || viewerEmail || '';
  const notary = req.notary_email
    || (req.notary_options || []).find((x) => x && !same(x, signer))
    || '';
  return { signer, notary };
}

/* '3 Sep 2026' — unambiguous, and no timezone maths on a date-only string. */
const fmtDate = (s) => {
  if (!s) return '–';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(s);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
};

const card = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 12,
  boxShadow: 'var(--shadow-xs)',
  padding: '18px 20px',
};

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
  textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6,
};

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '9px 11px',
  fontSize: 14.5, color: 'var(--text-1)', background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)', borderRadius: 8,
  fontFamily: 'inherit',
};

/* ── The facts somebody needs to make the decision ── */
export function ApplicationSummary({ request, context }) {
  // ⚠️ WHICH SIDE WINS, AND WHY. Two different answers, on purpose:
  //
  //   Job name and amount come from SUMMIT. Its billing lines are the frozen
  //   copy -- billing_period_lines.frozen_* with a COALESCE onto the live SOV
  //   -- which is the freeze that exists precisely so a finalised application
  //   keeps stating what it stated. The agent never receives a project name or
  //   a total; deriving one from the sov_lines payload would invent a second
  //   definition of "the amount of this pay app", and there is already one.
  //
  //   Invoice date, application number and project manager come from the
  //   REQUEST. The agent captured those when it generated the pdf, so they are
  //   what the DOCUMENT says -- and the invoice date in particular is editable
  //   in Summit after the fact.
  const name = context?.project_name || request.project_name;
  const amount = context && context.amount != null
    ? context.amount
    : (request.amount != null ? request.amount : null);
  // Someone changed the invoice date in Summit after this pdf was generated,
  // so the document and the due date below disagree. Worth saying out loud on
  // a screen about a financial document rather than quietly showing one.
  const dateMoved = Boolean(request.invoice_date && context?.invoice_date
    && request.invoice_date !== context.invoice_date);
  const rows = [
    ['Job', [context?.project_number, name].filter(Boolean).join(' — ')
      || (request.project_id ? `CE project ${request.project_id}` : '–')],
    ['Customer', context?.client_name || '–'],
    ['Project manager', request.project_manager || context?.project_manager || '–'],
    ['Billing', context?.period_name
      || (request.period_end_date ? `Period ending ${fmtDate(request.period_end_date)}` : '–')],
    ['Application no.', request.application_number != null ? `#${request.application_number}` : '–'],
    ['Invoice date', fmtDate(request.invoice_date || context?.invoice_date)],
    ['Payment due', fmtDate(context?.due_date)],
  ];
  return (
    <div style={card}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 16, flexWrap: 'wrap', marginBottom: 14,
      }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: 'var(--text-4)' }}>
            Pay application
          </div>
          <div style={{ fontSize: 21, fontWeight: 600, color: 'var(--text-1)',
            letterSpacing: '-0.015em', marginTop: 2 }}>
            {name || `CE project ${request.project_id || '?'}`}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: 'var(--text-4)' }}>
            Amount this application
          </div>
          <div style={{ fontSize: 24, fontWeight: 650, color: 'var(--text-1)',
            letterSpacing: '-0.02em', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
            {amount == null ? '\u2014' : formatMoney(amount)}
          </div>
          {context && context.retainage > 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>
              {formatMoney(context.retainage)} retainage held
            </div>
          ) : null}
        </div>
      </div>

      {request.is_revision ? (
        <div style={{
          display: 'inline-block', marginBottom: 12, padding: '3px 10px',
          borderRadius: 999, fontSize: 12.5, fontWeight: 600,
          color: 'var(--warning)',
          background: 'var(--warning-soft)',
          border: '1px solid color-mix(in oklch, var(--warning) 22%, transparent)',
        }}>
          Revision — this replaces an earlier application
        </div>
      ) : null}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: '12px 24px',
      }}>
        {rows.map(([k, v]) => (
          <div key={k}>
            <div style={{ fontSize: 12, color: 'var(--text-4)', marginBottom: 1 }}>{k}</div>
            <div style={{ fontSize: 14.5, color: 'var(--text-2)' }}>{v}</div>
          </div>
        ))}
      </div>

      {!context ? (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-3)' }}>
          Summit has no billing period matching this request, so the job name,
          amount, customer and payment due date are unavailable. You can still
          route it — the document itself is already generated.
        </div>
      ) : null}

      {dateMoved ? (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--warning)', lineHeight: 1.5 }}>
          The document was generated with an invoice date of{' '}
          {fmtDate(request.invoice_date)}, but Summit now shows{' '}
          {fmtDate(context.invoice_date)}. The payment due date above follows
          Summit. Re-generate the application if the printed date needs to match.
        </div>
      ) : null}
    </div>
  );
}

/* ── Already handled: a receipt, not a form ── */
export function AlreadyHandled({ request }) {
  // ⚠️ 'routed' IS NOT A FINISHED STATE. The row is claimed but no envelope
  // has been recorded, and that lasts seconds in the normal case. If it
  // persists, the agent died mid-send: nothing reminds (reminders only chase
  // 'pending'), a re-send is refused as "already handled", and a replay cannot
  // reopen it. So this must read as in-progress with a way out, not as a
  // receipt -- the agent's stuck sweep alerts accounting after 15 minutes.
  const SENT = {
    routed: ['Still being sent',
      'The signer and notary are set and the envelope is going out. This '
      + 'normally takes a few seconds — reload in a minute. If it still says '
      + 'this, tell IT: the send may have been interrupted, and somebody has '
      + 'to check Dropbox Sign before it is tried again.'],
    sent: ['Sent for signature', 'Dropbox Sign has it. Both people have been emailed.'],
    signed: ['Signed', 'Everyone has signed. The executed copy is filed under the '
      + "project's Documents tab."],
    cancelled: ['Cancelled', 'This request was cancelled. Nothing was sent.'],
  };
  const [title, body] = SENT[request.status] || ['Already handled',
    `This request is in state "${request.status}".`];
  return (
    <div style={{ ...card, borderColor: 'color-mix(in oklch, var(--success) 35%, transparent)' }}>
      <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ fontSize: 14.5, color: 'var(--text-2)', marginBottom: 12 }}>{body}</div>
      <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
        {request.signer_email ? (
          <div><span style={{ color: 'var(--text-4)' }}>Signing: </span>
            <span style={{ color: 'var(--text-1)' }}>{request.signer_email}</span></div>
        ) : null}
        {request.notary_email ? (
          <div><span style={{ color: 'var(--text-4)' }}>Notarising: </span>
            <span style={{ color: 'var(--text-1)' }}>{request.notary_email}</span></div>
        ) : null}
      </div>
      {request.status === 'routed' ? (
        <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 12 }}>
          Nothing further is needed from you unless this message is still here
          in a few minutes.
        </div>
      ) : null}
    </div>
  );
}

export default function AfpRoutingPage({ currentUser }) {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [signer, setSigner] = useState('');
  const [notary, setNotary] = useState('');
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sent, setSent] = useState(null);

  const load = useCallback(async () => {
    setState({ loading: true, error: null, data: null });
    try {
      const data = await getAfpRouting(token);
      setState({ loading: false, error: null, data });
      // Seed the fields so the common case is one glance and one click.
      const { signer: s0, notary: n0 } = seedPair(data.request, currentUser?.email);
      setSigner(s0);
      setNotary(n0);
    } catch (err) {
      setState({ loading: false, error: err.message || 'Could not load this request', data: null });
    }
  }, [token, currentUser]);

  useEffect(() => { load(); }, [load]);

  const request = state.data?.request;
  const context = state.data?.context;

  const options = useMemo(() => {
    const all = [
      ...((request?.position_a_options) || []),
      ...((request?.notary_options) || []),
      currentUser?.email,
    ].filter(Boolean);
    return [...new Set(all.map((s) => s.trim()))];
  }, [request, currentUser]);

  const sameParty = isEmail(signer) && isEmail(notary) && same(signer, notary);
  const problem = routingProblem(signer, notary);

  /** Put the viewer in one slot without wiping the other. */
  const takeRole = (role) => {
    const me = (currentUser?.email || '').trim();
    if (!me) return;
    setTouched(true);
    if (role === 'signer') {
      setSigner(me);
      if (same(notary, me)) setNotary('');
    } else {
      setNotary(me);
      if (same(signer, me)) setSigner('');
    }
  };

  const submit = async () => {
    setTouched(true);
    if (problem) return;
    setSending(true);
    setSendError(null);
    try {
      const result = await sendAfpRouting(token, signer.trim(), notary.trim());
      setSent(result);
    } catch (err) {
      setSendError(err.message || 'The envelope could not be sent.');
      // A 409 means somebody else answered this link while this page was
      // open. Reload so the screen stops offering a form for a decision that
      // has already been made.
      if (err.status === 409) { setSendError(null); load(); }
    } finally {
      setSending(false);
    }
  };

  const shell = (children) => (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 20px 60px',
      display: 'flex', flexDirection: 'column', gap: 16 }}>
      {children}
    </div>
  );

  if (state.loading) {
    return shell(<div style={{ ...card, color: 'var(--text-3)' }}>Loading this pay application…</div>);
  }

  if (state.error) {
    return shell(
      <div style={{ ...card, borderColor: 'color-mix(in oklch, var(--danger) 35%, transparent)' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6 }}>
          This routing link could not be opened
        </div>
        <div style={{ fontSize: 14.5, color: 'var(--text-2)' }}>{state.error}</div>
        <button type="button" onClick={load} style={{
          marginTop: 14, padding: '8px 16px', fontSize: 14, fontWeight: 550,
          color: 'var(--text-1)', background: 'var(--bg-surface-2)',
          border: '1px solid var(--border-default)', borderRadius: 8, cursor: 'pointer',
        }}>Try again</button>
      </div>,
    );
  }

  if (sent) {
    return shell(
      <>
        <ApplicationSummary request={request} context={context} />
        <div style={{ ...card, borderColor: 'color-mix(in oklch, var(--success) 35%, transparent)' }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 }}>
            Sent for signature
          </div>
          <div style={{ fontSize: 14.5, color: 'var(--text-2)', marginBottom: 12 }}>
            Dropbox Sign has emailed both people. The executed copy files itself
            under the project&rsquo;s Documents tab once everyone has signed.
          </div>
          <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
            <div><span style={{ color: 'var(--text-4)' }}>Signing: </span>
              <span style={{ color: 'var(--text-1)' }}>{sent.signer_email || signer}</span></div>
            <div><span style={{ color: 'var(--text-4)' }}>Notarising: </span>
              <span style={{ color: 'var(--text-1)' }}>{sent.notary_email || notary}</span></div>
          </div>
          {sent.test_mode ? (
            <div style={{ marginTop: 12, fontSize: 13, color: 'var(--warning)' }}>
              Sent in Dropbox Sign test mode — the document carries a watermark
              and is not legally binding.
            </div>
          ) : null}
        </div>
      </>,
    );
  }

  if (request && request.status !== 'pending') {
    return shell(
      <>
        <ApplicationSummary request={request} context={context} />
        <AlreadyHandled request={request} />
      </>,
    );
  }

  const me = (currentUser?.email || '').trim().toLowerCase();
  const iAmSigner = me && same(signer, me);
  const iAmNotary = me && same(notary, me);

  const roleButton = (active, onClick, children) => (
    <button type="button" onClick={onClick} style={{
      padding: '7px 14px', fontSize: 13.5, fontWeight: 550, cursor: 'pointer',
      borderRadius: 999, fontFamily: 'inherit',
      color: active ? 'var(--accent-text)' : 'var(--text-2)',
      background: active ? 'var(--accent-soft)' : 'var(--bg-surface-2)',
      border: `1px solid ${active ? 'var(--accent-ring)' : 'var(--border-default)'}`,
    }}>{children}</button>
  );

  return shell(
    <>
      <ApplicationSummary request={request} context={context} />

      <div style={card}>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 }}>
          Who is signing this one?
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-3)', marginBottom: 16, lineHeight: 1.5 }}>
          The signer and the notary have to be in the office together, so pick
          whoever is actually here. Either field can be typed if none of the
          usual people are around.
        </div>

        {currentUser?.email ? (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {roleButton(Boolean(iAmSigner), () => takeRole('signer'), 'I\u2019m signing')}
            {roleButton(Boolean(iAmNotary), () => takeRole('notary'), 'I\u2019m notarising')}
          </div>
        ) : null}

        {/* One datalist, shared. Either configured person can end up in either
            slot -- whoever is in the building today. */}
        <datalist id="afp-routing-people">
          {options.map((o) => <option key={o} value={o} />)}
        </datalist>

        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label style={labelStyle} htmlFor="afp-signer">Signing the application</label>
            <input
              id="afp-signer" type="email" list="afp-routing-people"
              value={signer} placeholder="name@e3es.com"
              onChange={(e) => { setSigner(e.target.value); setTouched(true); }}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="afp-notary">Notarising it</label>
            <input
              id="afp-notary" type="email" list="afp-routing-people"
              value={notary} placeholder="name@e3es.com"
              onChange={(e) => { setNotary(e.target.value); setTouched(true); }}
              style={{
                ...inputStyle,
                borderColor: sameParty ? 'var(--danger)' : inputStyle.borderColor,
              }}
            />
          </div>
        </div>

        {touched && problem ? (
          <div style={{ marginTop: 12, fontSize: 13.5, color: 'var(--danger)', lineHeight: 1.5 }}>
            {problem}
          </div>
        ) : null}

        {sendError ? (
          <div style={{
            marginTop: 14, padding: '10px 12px', borderRadius: 8, fontSize: 13.5,
            lineHeight: 1.5, color: 'var(--danger)', background: 'var(--danger-soft)',
            border: '1px solid color-mix(in oklch, var(--danger) 25%, transparent)',
          }}>{sendError}</div>
        ) : null}

        <div style={{
          marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        }}>
          <button
            type="button" onClick={submit}
            disabled={sending || Boolean(problem)}
            style={{
              padding: '10px 20px', fontSize: 14.5, fontWeight: 600,
              fontFamily: 'inherit', borderRadius: 9,
              color: 'var(--accent-text)',
              background: 'var(--accent-soft)',
              border: '1px solid var(--accent-ring)',
              cursor: (sending || problem) ? 'not-allowed' : 'pointer',
              opacity: (sending || problem) ? 0.55 : 1,
            }}
          >
            {sending ? 'Sending…' : 'Send for signature'}
          </button>
          <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.45, flex: 1, minWidth: 220 }}>
            {problem
              ? 'Fill both fields with two different people to continue.'
              : `Dropbox Sign will email ${signer.trim()} to sign, then `
                + `${notary.trim()} to notarise. This cannot be undone from here.`}
          </div>
        </div>
      </div>
    </>,
  );
}
